import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TenantContextService } from '../../core/services';
import { BadgeComponent } from '../../shared';
import { AuthService } from '../../core/auth/auth.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSelectModule,
    MatFormFieldModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    BadgeComponent,
  ],
  template: `
    <header class="header">
      <h2 class="header-title">
        <ng-content></ng-content>
      </h2>
      <div class="tenant-switch">
        <span class="tenant-label">Tenant:</span>
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tenant-select">
          <mat-select [value]="ctx.activeTenantId()" (selectionChange)="ctx.switchTenant($event.value)">
            @for (t of ctx.tenants(); track t.id) {
              <mat-option [value]="t.id">{{ t.name }} ({{ t.code }})</mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (ctx.activeTenant(); as t) {
          <app-badge [status]="t.status" [small]="true" />
        }
      </div>

      @if (auth.isLoggedIn()) {
        <button mat-button class="user-button" [matMenuTriggerFor]="userMenu" aria-label="User menu">
          <span class="user-avatar">{{ auth.initials() }}</span>
          <span class="user-name">{{ auth.displayName() }}</span>
          <mat-icon class="caret">expand_more</mat-icon>
        </button>
        <mat-menu #userMenu="matMenu" xPosition="before">
          <button mat-menu-item disabled>
            <mat-icon>person</mat-icon>
            <span>{{ auth.account()?.username }}</span>
          </button>
          <button mat-menu-item (click)="auth.logout()">
            <mat-icon>logout</mat-icon>
            <span>Sign out</span>
          </button>
        </mat-menu>
      }
    </header>
  `,
  styles: [`
    .header {
      height: 58px; min-height: 58px; padding: 0 24px;
      display: flex; align-items: center; gap: 16px;
      border-bottom: 1px solid #21262d; background: #161b22;
    }
    .header-title { font-size: 14px; font-weight: 600; flex: 1; margin: 0; }
    .tenant-switch { display: flex; align-items: center; gap: 8px; }
    .tenant-label { font-size: 11px; color: #6e7681; }
    .tenant-select {
      width: 200px;
    }
    :host ::ng-deep .tenant-select .mat-mdc-form-field-wrapper {
      padding: 0;
    }
    :host ::ng-deep .tenant-select .mdc-notched-outline__leading,
    :host ::ng-deep .tenant-select .mdc-notched-outline__notch,
    :host ::ng-deep .tenant-select .mdc-notched-outline__trailing {
      border-color: #30363d !important;
    }
    :host ::ng-deep .tenant-select .mat-mdc-select-value {
      font-size: 12px; font-weight: 600; color: #e6edf3;
    }
    :host ::ng-deep .tenant-select .mat-mdc-form-field-infix {
      padding: 6px 0 !important;
      min-height: 32px !important;
    }
    .user-button {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 4px 10px; border-radius: 8px;
      color: #e6edf3;
    }
    .user-avatar {
      width: 28px; height: 28px; border-radius: 50%;
      background: linear-gradient(135deg, #3fb950 0%, #238636 100%);
      color: #fff; font-size: 11px; font-weight: 700;
      display: inline-flex; align-items: center; justify-content: center;
      letter-spacing: .5px;
    }
    .user-name {
      font-size: 12px; font-weight: 600; max-width: 140px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .caret { font-size: 16px; width: 16px; height: 16px; color: #6e7681; }
  `],
})
export class HeaderComponent {
  ctx = inject(TenantContextService);
  auth = inject(AuthService);
}
